import { IFillSentenceGapTask } from 'common/modules/ReactShared';
import { t } from 'i18n';
import React, { useCallback, useState } from 'react';
import DashedPagination from 'students/views/shared/components/DashedPagination';
import styled from 'styled-components';
import ItemResultScreen from './components/ItemResultScreen';
import SentenceGapItem, {
  IItemSelectAnswerPayload,
  ISelectAnswerPayload
} from './components/SentenceGapItem';

enum Screen {
  Item = 'item',
  Result = 'result'
}

interface IProps {
  task: IFillSentenceGapTask;
  onSelectAnswer: (payload: ISelectAnswerPayload) => void;
  onComplete: () => void;
  onSkip: (itemId: number) => void;
}

const PlayFillSentenceGapMainScreen: React.FC<IProps> = ({
  task,
  onSelectAnswer,
  onComplete,
  onSkip
}) => {
  const { items } = task;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentScreen, setCurrentScreen] = useState<Screen>(Screen.Item);

  const currentItem = items[currentIndex];

  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentIndex + 1 === items.length) {
      handleComplete();
    } else {
      setCurrentIndex((currentIndex) => currentIndex + 1);
    }
  }, [currentIndex, handleComplete, items.length]);

  const handleSelectAnswer = (payload: IItemSelectAnswerPayload) => {
    setCurrentScreen(Screen.Result);

    onSelectAnswer({ ...payload, itemIndex: currentIndex });
  };

  const handleItemFinished = useCallback(() => {
    setCurrentScreen(Screen.Item);
    handleNext();
  }, [handleNext]);

  const handleItemNext = () => {
    onSkip(currentItem.id);

    handleNext();
  };

  const handleItemPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((currentIndex) => currentIndex - 1);
      setCurrentScreen(Screen.Item);
    }
  };

  const renderScreen = (screen: Screen): JSX.Element | null => {
    switch (screen) {
      case Screen.Item:
        return (
          <>
            <SSentenceGapItemWrapper>
              <SentenceGapItem
                key={currentItem.id}
                item={currentItem}
                onSelectAnswer={handleSelectAnswer}
              />
            </SSentenceGapItemWrapper>

            <DashedPagination
              itemsNumber={items.length}
              currentIndex={currentIndex}
              taskId={task.id}
              onNextItem={handleItemNext}
              onPrevItem={handleItemPrev}
            />
          </>
        );
      case Screen.Result:
        return <ItemResultScreen item={currentItem} onFinish={handleItemFinished} />;

      default:
        return null;
    }
  };

  // Guard against a missing/malformed item (e.g. a degenerate last item).
  // Without this, reading currentItem.id / passing it to child screens throws
  // and the crash is swallowed by the error boundary, leaving the student stuck
  // on a screen where "nothing happens" (Sentry LINGU-STUDENT-APP-143).
  if (!currentItem) {
    return (
      <SMissingItemMessage>
        {t('frontend.lesson_task.tasks.play.fill_gap.item_unavailable', {
          defaultValue:
            "This exercise couldn't be loaded. Please move on to the next task."
        })}
      </SMissingItemMessage>
    );
  }

  return renderScreen(currentScreen);
};

export default PlayFillSentenceGapMainScreen;

const SSentenceGapItemWrapper = styled.div`
  flex-grow: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SMissingItemMessage = styled.div`
  flex-grow: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px;
  text-align: center;
  color: var(--ln-ink-on-dark);
  font-size: 1.25rem;
`;
